import { buildUrunCategoryTreeModel } from './urunCategoryTree';

const oid = (x) => String(x || '').trim();

export function emptySelection() {
  return { leafIds: [], productIds: [] };
}

export function fromServiceArea(area) {
  const leafIds = (area?.yetkiliKategoriIds || []).map(oid).filter(Boolean);
  const fromKats = (area?.yetkiliKategoriler || [])
    .map((k) => oid(k?.id || k?._id))
    .filter(Boolean);
  const productIds = (area?.yetkiliUrunIcerikIds || []).map(oid).filter(Boolean);
  const leaves = leafIds.length ? leafIds : fromKats;
  if (leaves.length === 0 && productIds.length === 0) {
    return emptySelection();
  }
  return { leafIds: leaves, productIds };
}

/**
 * UI seçimini API gövdesine çevirir (boş ikisi ⇒ tüm ürünler, alanları gönderme).
 */
export function toApiPayload(selection, tree, productsByLeaf) {
  const leafIds = new Set((selection?.leafIds || []).map(oid).filter(Boolean));
  const productIds = new Set((selection?.productIds || []).map(oid).filter(Boolean));

  if (leafIds.size === 0 && productIds.size === 0) {
    return {};
  }

  for (const lid of [...leafIds]) {
    const prods = productsByLeaf.get(lid) || [];
    if (prods.length > 0) {
      const allSelected = prods.every((p) => productIds.has(oid(p.id)));
      if (allSelected) {
        prods.forEach((p) => productIds.delete(oid(p.id)));
      }
    }
  }

  const payload = {};
  if (leafIds.size > 0) {
    payload.yetkiliKategoriIds = [...leafIds];
  }
  if (productIds.size > 0) {
    payload.yetkiliUrunIcerikIds = [...productIds];
  }
  return payload;
}

export function buildProductsByLeaf(urunContents) {
  const map = new Map();
  for (const p of urunContents || []) {
    const kid = oid(p.kategoriId || p.kategori?.id || p.kategori?._id);
    if (!kid) continue;
    if (!map.has(kid)) map.set(kid, []);
    map.get(kid).push({
      id: oid(p.id || p._id),
      ad: (p.icerikAdi || p.urunAdi || '—').trim(),
    });
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => a.ad.localeCompare(b.ad, 'tr'));
  }
  return map;
}

export function batchCheckOptions(selection, tree, productsByLeaf, excludeTechServiceId) {
  const payload = toApiPayload(selection, tree, productsByLeaf);
  const opts = { ...payload };
  if (excludeTechServiceId && /^[0-9a-fA-F]{24}$/.test(String(excludeTechServiceId).trim())) {
    opts.excludeTechServiceId = String(excludeTechServiceId).trim();
  }
  return opts;
}

export function getNodeCheckState(nodeId, selection, tree, productsByLeaf) {
  const cid = oid(nodeId);
  const leafIds = new Set((selection?.leafIds || []).map(oid));
  const productIds = new Set((selection?.productIds || []).map(oid));

  if (tree.leafIds.has(cid)) {
    const prods = productsByLeaf.get(cid) || [];
    if (prods.length === 0) {
      return { checked: leafIds.has(cid), indeterminate: false };
    }
    const selectedCount = prods.filter((p) => productIds.has(oid(p.id)) || leafIds.has(cid)).length;
    if (leafIds.has(cid)) return { checked: true, indeterminate: false };
    if (selectedCount === 0) return { checked: false, indeterminate: false };
    if (selectedCount === prods.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  }

  const under = tree.leavesUnder(cid);
  if (under.size === 0) return { checked: false, indeterminate: false };

  let full = 0;
  let any = 0;
  for (const leaf of under) {
    const st = getNodeCheckState(leaf, selection, tree, productsByLeaf);
    if (st.checked) full += 1;
    if (st.checked || st.indeterminate) any += 1;
  }
  if (full === under.size) return { checked: true, indeterminate: false };
  if (any > 0) return { checked: false, indeterminate: true };
  return { checked: false, indeterminate: false };
}

export function setNodeChecked(nodeId, checked, selection, tree, productsByLeaf) {
  const cid = oid(nodeId);
  const next = {
    leafIds: [...(selection?.leafIds || [])].map(oid),
    productIds: [...(selection?.productIds || [])].map(oid),
  };
  const leafSet = new Set(next.leafIds);
  const prodSet = new Set(next.productIds);

  const applyLeaf = (leafId, on) => {
    const lid = oid(leafId);
    const prods = productsByLeaf.get(lid) || [];
    if (on) {
      leafSet.add(lid);
      prods.forEach((p) => prodSet.delete(oid(p.id)));
    } else {
      leafSet.delete(lid);
      prods.forEach((p) => prodSet.delete(oid(p.id)));
    }
  };

  if (tree.leafIds.has(cid)) {
    applyLeaf(cid, checked);
  } else {
    for (const leaf of tree.leavesUnder(cid)) {
      applyLeaf(leaf, checked);
    }
  }

  return { leafIds: [...leafSet], productIds: [...prodSet] };
}

export function setProductChecked(productId, leafId, checked, selection) {
  const pid = oid(productId);
  const lid = oid(leafId);
  const leafSet = new Set((selection?.leafIds || []).map(oid));
  const prodSet = new Set((selection?.productIds || []).map(oid));
  leafSet.delete(lid);
  if (checked) prodSet.add(pid);
  else prodSet.delete(pid);
  return { leafIds: [...leafSet], productIds: [...prodSet] };
}

export function buildTreeFromFlat(flatCategories) {
  return buildUrunCategoryTreeModel(flatCategories);
}
